import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, UserPlus, Check, Film, MessageCircle } from 'lucide-react';
import { useRealtime } from '../../context/RealtimeContext';
import type { AppNotification } from '../../types';

function iconFor(type: AppNotification['type']) {
  switch (type) {
    case 'friend_request':
      return <UserPlus className="h-4 w-4 text-primary-500" />;
    case 'friend_accept':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'movie':
      return <Film className="h-4 w-4 text-primary-500" />;
    default:
      return <MessageCircle className="h-4 w-4 text-primary-500" />;
  }
}

function labelFor(n: AppNotification): { title: string; sub: string; href: string } {
  const name = n.data?.user?.name || n.data?.fromUser?.name || n.data?.user?.email || 'Someone';
  switch (n.type) {
    case 'friend_request':
      return { title: `${name} sent a friend request`, sub: 'Tap to respond', href: '/friends' };
    case 'friend_accept':
      return { title: `${name} accepted your request`, sub: 'You are now friends', href: '/friends' };
    case 'movie':
      return {
        title: `${name} sent you a movie`,
        sub: n.data?.movie?.title || 'Tap to view',
        href: '/messages',
      };
    default:
      return { title: 'New message', sub: '', href: '/messages' };
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function NotificationBell() {
  const { notifications, totalUnread, markAllRead } = useRealtime();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && totalUnread > 0) markAllRead();
        }}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        {totalUnread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-label="Close notifications"
          />
          <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
              <button
                type="button"
                onClick={() => navigate('/friends')}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Friends
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No notifications yet
                </p>
              ) : (
                notifications.map((n) => {
                  const { title, sub, href } = labelFor(n);
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(href);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        n.isRead ? '' : 'bg-primary-50/50 dark:bg-primary-900/10'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">{iconFor(n.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white truncate">{title}</p>
                        {sub && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{sub}</p>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {timeAgo(n.createdAt)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
